import React, { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// "Sign in with Google" via Google Identity Services (GIS), config-gated on
// REACT_APP_GOOGLE_CLIENT_ID — the same flag that gates the backend's
// /api/auth/google (which 503s with google_auth_not_configured until the
// maintainer supplies GOOGLE_CLIENT_ID). Without the env var this renders a
// small "coming soon" note; with it, the GIS script is loaded on demand (no
// npm dependency) and the official button is rendered. The GIS callback hands
// us an ID-token credential which loginWithGoogle() posts to the backend.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GIS_SCRIPT_ID = 'google-identity-services';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

// `onCredential`, when given, receives the ID-token credential INSTEAD of
// signing in with it — the fresh sign-in that confirms deleting an account
// without a password (issue 782).
const GoogleSignInButton = ({ onSuccess, onError, onCredential }) => {
  const { loginWithGoogle } = useAuth();
  const containerRef = useRef(null);

  // Keep the latest handlers in refs so the GIS callback (registered once)
  // never goes stale.
  const handlersRef = useRef({ loginWithGoogle, onSuccess, onError, onCredential });
  useEffect(() => {
    handlersRef.current = { loginWithGoogle, onSuccess, onError, onCredential };
  }, [loginWithGoogle, onSuccess, onError, onCredential]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;

    const handleCredential = async (response) => {
      const { loginWithGoogle: doLogin, onSuccess: ok, onError: fail, onCredential: hand } = handlersRef.current;
      if (hand) { if (!cancelled) hand(response.credential); return; }
      try {
        await doLogin(response.credential);
        if (!cancelled && ok) ok();
      } catch (err) {
        if (cancelled) return;
        const code = err.response?.data?.error;
        const message = code === 'google_auth_not_configured'
          ? 'Google sign-in is not available yet.'
          : 'Google sign-in failed. Please try again.';
        if (fail) fail(message);
      }
    };

    const init = () => {
      if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential,
      });
      // Match the surrounding form: dark button in dark mode, and full form
      // width (GIS caps width at 400px). Read the container's laid-out width so
      // it lines up with the LOG IN button rather than a fixed 280px.
      const container = containerRef.current;
      const isDark = document.documentElement.classList.contains('dark');
      const width = Math.min(Math.max(container.offsetWidth || 320, 200), 400);
      window.google.accounts.id.renderButton(container, {
        theme: isDark ? 'filled_black' : 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width,
      });
    };

    if (window.google?.accounts?.id) {
      init();
      return () => {
        cancelled = true;
      };
    }

    let script = document.getElementById(GIS_SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = GIS_SCRIPT_ID;
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', init);
    return () => {
      cancelled = true;
      script.removeEventListener('load', init);
    };
  }, []);

  if (!GOOGLE_CLIENT_ID) {
    return (
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        Sign in with Google — coming soon.
      </p>
    );
  }

  return <div ref={containerRef} data-testid="google-signin" className="flex justify-center" />;
};

export default GoogleSignInButton;
